import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMapEvents } from 'react-leaflet';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import 'leaflet/dist/leaflet.css';
import './ImpactMap.css';

// Public viewer for the tree observation dataset. Hits /api/viewer/trees/,
// which returns the strict-allowlist public shape — no PII, no owner info, no
// notes. Read-only, no auth, viewport-scoped fetches with a small pagination
// cap so the map cannot pull the whole dataset in one request.

const API_URL = '/api/viewer/trees/';

const FALLBACK_CENTER = [40.0, -105.5]; // Front Range, CO
const FALLBACK_ZOOM = 8;

// The viewer only shows tree observations at reasonable neighborhood-to-county
// scale. Below zoom 7 the bbox area cap trips (5000 km² server-side) and the
// map would just show an error banner — instead we skip the fetch and show a
// hint to zoom in.
const MIN_FETCH_ZOOM = 7;

const STATUS_COLORS = {
  healthy_monitoring: '#1a6b3c',
  suspected: '#c47f17',
  confirmed_active: '#c1362c',
  dead_standing: '#5a5a5a',
  dead_fallen: '#2f2f2f',
  hazard: '#7a1f13',
  treated: '#3968b0',
};

const STATUS_LABELS = {
  healthy_monitoring: 'Healthy — monitoring',
  suspected: 'Suspected',
  confirmed_active: 'Confirmed active',
  dead_standing: 'Dead (standing)',
  dead_fallen: 'Dead (fallen)',
  hazard: 'Hazard',
  treated: 'Treated',
};

const SPECIES_LABELS = {
  ponderosa: 'Ponderosa pine',
  lodgepole: 'Lodgepole pine',
  limber: 'Limber pine',
  bristlecone: 'Bristlecone pine',
  whitebark: 'Whitebark pine',
  scotch: 'Scotch pine',
  douglas_fir: 'Douglas-fir',
  engelmann_spruce: 'Engelmann spruce',
  blue_spruce: 'Blue spruce',
  subalpine_fir: 'Subalpine fir',
  aspen: 'Aspen',
  juniper: 'Juniper',
  other: 'Other / unknown',
};

const SPONSOR_TYPE_LABELS = {
  corporate: 'Corporate',
  government: 'Government',
  nonprofit: 'Nonprofit',
  foundation: 'Foundation / grant',
  individual: 'Individual / donor',
  other: 'Other',
};

const LAYERS = {
  esri: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri',
    maxZoom: 19,
  },
  osm: {
    label: 'Street',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
  },
};

// The public API returns locations snapped to a ~110 m grid, so every tree in
// a grid cell shares a coordinate. Group them into one marker per cell.
function groupByLocation(trees) {
  const groups = new Map();
  for (const t of trees) {
    const key = `${t.latitude},${t.longitude}`;
    let g = groups.get(key);
    if (!g) {
      g = { key, center: [Number(t.latitude), Number(t.longitude)], trees: [], statusCounts: {} };
      groups.set(key, g);
    }
    g.trees.push(t);
    g.statusCounts[t.status] = (g.statusCounts[t.status] || 0) + 1;
  }
  for (const g of groups.values()) {
    g.dominantStatus = Object.entries(g.statusCounts).sort((a, b) => b[1] - a[1])[0][0];
  }
  return [...groups.values()];
}

function markerRadius(count) {
  return count === 1 ? 6 : Math.min(18, 7 + Math.log2(count) * 2);
}

function countLabel(counts, labels) {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([code, n]) => `${labels[code] || code} (${n})`)
    .join(', ');
}

function GroupPopup({ group }) {
  const { trees } = group;
  if (trees.length === 1) {
    const t = trees[0];
    return (
      <div className="impact-popup">
        <div className="impact-popup-species">
          {SPECIES_LABELS[t.species] || t.species || 'Tree'}
        </div>
        <div className="impact-popup-status">
          <span
            className="impact-popup-dot"
            style={{ background: STATUS_COLORS[t.status] || '#888' }}
          />
          {STATUS_LABELS[t.status] || t.status}
        </div>
        {t.sponsor && (
          <div className="impact-popup-row">
            <span className="impact-popup-label">Sponsor</span>
            <span>{t.sponsor}</span>
            {t.sponsor_type && SPONSOR_TYPE_LABELS[t.sponsor_type] && (
              <span className="text-muted"> · {SPONSOR_TYPE_LABELS[t.sponsor_type]}</span>
            )}
          </div>
        )}
        {t.observed_at && (
          <div className="impact-popup-row">
            <span className="impact-popup-label">Observed</span>
            <span>{new Date(t.observed_at).toLocaleDateString()}</span>
          </div>
        )}
      </div>
    );
  }

  const speciesCounts = {};
  const sponsors = new Set();
  const times = [];
  for (const t of trees) {
    speciesCounts[t.species] = (speciesCounts[t.species] || 0) + 1;
    if (t.sponsor) sponsors.add(t.sponsor);
    if (t.observed_at) times.push(new Date(t.observed_at).getTime());
  }
  const first = times.length ? new Date(Math.min(...times)).toLocaleDateString() : null;
  const last = times.length ? new Date(Math.max(...times)).toLocaleDateString() : null;

  return (
    <div className="impact-popup">
      <div className="impact-popup-species">{trees.length.toLocaleString()} trees in this area</div>
      {Object.entries(group.statusCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([code, n]) => (
          <div className="impact-popup-status" key={code}>
            <span className="impact-popup-dot" style={{ background: STATUS_COLORS[code] || '#888' }} />
            {STATUS_LABELS[code] || code}: {n}
          </div>
        ))}
      <div className="impact-popup-row">
        <span className="impact-popup-label">Species</span>
        <span>{countLabel(speciesCounts, SPECIES_LABELS)}</span>
      </div>
      {sponsors.size > 0 && (
        <div className="impact-popup-row">
          <span className="impact-popup-label">Sponsors</span>
          <span>{[...sponsors].join(', ')}</span>
        </div>
      )}
      {first && (
        <div className="impact-popup-row">
          <span className="impact-popup-label">Observed</span>
          <span>{first === last ? first : `${first} – ${last}`}</span>
        </div>
      )}
    </div>
  );
}

function boundsToBboxParam(bounds) {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return `${sw.lat.toFixed(6)},${sw.lng.toFixed(6)},${ne.lat.toFixed(6)},${ne.lng.toFixed(6)}`;
}

// Listens to Leaflet's moveend/zoomend and calls onViewportChange with the
// current bounds + zoom. Debounced so a pan-and-release fires one fetch, not
// many.
function ViewportListener({ onViewportChange, delayMs = 400 }) {
  const timerRef = useRef(null);
  const emit = useCallback((map) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onViewportChange(map.getBounds(), map.getZoom());
    }, delayMs);
  }, [onViewportChange, delayMs]);

  const map = useMapEvents({
    moveend: () => emit(map),
    zoomend: () => emit(map),
  });

  useEffect(() => {
    onViewportChange(map.getBounds(), map.getZoom());
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default function ImpactMap() {
  const [trees, setTrees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [zoomTooLow, setZoomTooLow] = useState(false);

  const [filters, setFilters] = useState({ species: '', status: '', sponsor_type: '' });
  const [activeLayer, setActiveLayer] = useState('esri');

  const abortRef = useRef(null);
  const lastBoundsRef = useRef(null);

  const fetchTrees = useCallback(async (bounds, zoom) => {
    if (zoom < MIN_FETCH_ZOOM) {
      setZoomTooLow(true);
      setTrees([]);
      setTotalCount(0);
      setError('');
      return;
    }
    setZoomTooLow(false);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        bbox: boundsToBboxParam(bounds),
        page_size: '500',
      });
      if (filters.species) params.set('species', filters.species);
      if (filters.status) params.set('status', filters.status);
      if (filters.sponsor_type) params.set('sponsor_type', filters.sponsor_type);

      const resp = await fetch(`${API_URL}?${params.toString()}`, { signal: controller.signal });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || body.detail || `Request failed (${resp.status})`);
      }
      const data = await resp.json();
      setTrees(Array.isArray(data.results) ? data.results : []);
      setTotalCount(typeof data.count === 'number' ? data.count : 0);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Could not load tree data.');
      setTrees([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleViewportChange = useCallback((bounds, zoom) => {
    lastBoundsRef.current = { bounds, zoom };
    fetchTrees(bounds, zoom);
  }, [fetchTrees]);

  // Re-fetch when the filter set changes, using the last-known viewport.
  useEffect(() => {
    if (lastBoundsRef.current) {
      fetchTrees(lastBoundsRef.current.bounds, lastBoundsRef.current.zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const statusBreakdown = useMemo(() => {
    const counts = {};
    for (const t of trees) {
      counts[t.status] = (counts[t.status] || 0) + 1;
    }
    return counts;
  }, [trees]);

  const groups = useMemo(() => groupByLocation(trees), [trees]);
  const truncated = totalCount > trees.length;

  const layer = LAYERS[activeLayer];

  return (
    <div className="impact-page">
      <Container fluid className="impact-header py-3">
        <Container>
          <Row className="align-items-center g-2">
            <Col>
              <h1 className="impact-title mb-0">Impact Map</h1>
              <div className="impact-subtitle">
                Live map of tree observations logged by our field crews and partners.
              </div>
            </Col>
            <Col xs="auto">
              <Badge bg="dark" className="impact-total-badge">
                {totalCount.toLocaleString()} in view
              </Badge>
            </Col>
          </Row>
        </Container>
      </Container>

      <Container fluid className="impact-body">
        <Row className="g-3">
          <Col lg={3} md={4}>
            <Card className="impact-filters mb-3">
              <Card.Body>
                <Card.Title as="h2" className="h6 text-uppercase text-muted mb-3">
                  Filters
                </Card.Title>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted mb-1">Species</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.species}
                    onChange={(e) => setFilters((f) => ({ ...f, species: e.target.value }))}
                  >
                    <option value="">All species</option>
                    {Object.entries(SPECIES_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted mb-1">Status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  >
                    <option value="">All statuses</option>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small text-muted mb-1">Sponsor type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.sponsor_type}
                    onChange={(e) => setFilters((f) => ({ ...f, sponsor_type: e.target.value }))}
                  >
                    <option value="">All sponsors</option>
                    {Object.entries(SPONSOR_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {(filters.species || filters.status || filters.sponsor_type) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary w-100 mt-2"
                    onClick={() => setFilters({ species: '', status: '', sponsor_type: '' })}
                  >
                    Clear filters
                  </button>
                )}
              </Card.Body>
            </Card>

            <Card className="impact-legend mb-3">
              <Card.Body>
                <Card.Title as="h2" className="h6 text-uppercase text-muted mb-3">
                  Status legend
                </Card.Title>
                {Object.entries(STATUS_LABELS).map(([code, label]) => (
                  <div className="impact-legend-row" key={code}>
                    <span className="impact-swatch" style={{ background: STATUS_COLORS[code] }} />
                    <span className="impact-legend-label">{label}</span>
                    <span className="impact-legend-count">
                      {statusBreakdown[code] ? statusBreakdown[code].toLocaleString() : ''}
                    </span>
                  </div>
                ))}
              </Card.Body>
            </Card>

            <Card className="impact-about">
              <Card.Body>
                <Card.Title as="h2" className="h6 text-uppercase text-muted mb-2">About the data</Card.Title>
                <p className="small mb-2">
                  Each dot marks trees observed by our field crews or reported by
                  partner landowners. To protect landowner privacy, locations are
                  approximate (rounded to about 110&nbsp;m), and trees in the same
                  area share one dot. Names, addresses, and contact details are
                  never published.
                </p>
                <p className="small mb-0 text-muted">
                  Data refreshes automatically as you pan and zoom.
                </p>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={9} md={8}>
            {error && (
              <Alert variant="danger" className="mb-2 py-2 small">{error}</Alert>
            )}
            {zoomTooLow && !error && (
              <Alert variant="info" className="mb-2 py-2 small">
                Zoom in to load tree observations for this area.
              </Alert>
            )}
            {truncated && !zoomTooLow && !error && (
              <Alert variant="info" className="mb-2 py-2 small">
                Showing the {trees.length.toLocaleString()} most recent of{' '}
                {totalCount.toLocaleString()} observations in view. Zoom in to see all of them.
              </Alert>
            )}
            <div className="impact-map-shell">
              <MapContainer
                center={FALLBACK_CENTER}
                zoom={FALLBACK_ZOOM}
                className="impact-map"
                scrollWheelZoom
              >
                <TileLayer
                  key={activeLayer}
                  url={layer.url}
                  attribution={layer.attribution}
                  maxZoom={layer.maxZoom}
                />
                <ViewportListener onViewportChange={handleViewportChange} />
                {groups.map((g) => (
                  <CircleMarker
                    key={g.key}
                    center={g.center}
                    radius={markerRadius(g.trees.length)}
                    pathOptions={{
                      color: '#fff',
                      weight: 1.5,
                      fillColor: STATUS_COLORS[g.dominantStatus] || '#888',
                      fillOpacity: 0.9,
                    }}
                  >
                    <Popup>
                      <GroupPopup group={g} />
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>

              <div className="impact-layer-picker">
                {Object.entries(LAYERS).map(([key, l]) => (
                  <button
                    key={key}
                    type="button"
                    className={`impact-layer-btn ${activeLayer === key ? 'active' : ''}`}
                    onClick={() => setActiveLayer(key)}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              {loading && (
                <div className="impact-loading-badge">
                  <Spinner animation="border" size="sm" />
                  <span>Loading…</span>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
